<template>
  <v-container class="pa-0">
    <v-row>
      <v-col cols="12" md="5">
        <v-select
          v-model="categoriesSelected"
          :items="categories"
          label="EVENT TYPE"
          multiple
          class="ml-4 categories"
          @change="handleChangeCategories"
        ></v-select>
      </v-col>
      <v-col cols="12" md="7" class="d-flex justify-end">
        <DateRangeSelector
          title="EVENTS"
          :items="timeRangeOptions"
          defaultContent
          v-model="activeTimeRange"
          @change="handleDateRangeChange"
          class="pull-right"
        ></DateRangeSelector>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <Loader v-if="loading" text="" color="white" />
        <DetailsListCluster
          v-if="itemsResults.length > 0"
          theme="dark"
          :items="itemsResults"
          :page="page"
          :pageSize="pageSize"
          :height-percent="100"
          :height-pixels="0"
          :show-primary-topics="true"
          :show-thumbs-down="true"
          :get-threshold-type-function="getThresholdTypeFunction"
          :scroll-to-top="scrollToTop"
          @get-data="getContent"
          :loader="loading"
          @loaded="loading = false"
        />
        <h4 class="text-center" v-if="!loading && itemsResults.length === 0">
          No articles found matching search criteria
        </h4>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { getArticles } from "@/services/search/wod.service";
import dayjs from "dayjs";
import lodashIsEqual from "lodash/isEqual";
import omit from "lodash/omit";
import lodashDebounce from "lodash/debounce";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_USER } from "@/store";
import { TIME_OPTIONS } from "@/common/constants/assetManagement";
import { getRelativeDate } from "@/common/date.utilities";

export default {
  name: "AssetsPaneVerifiedEvents",
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    DateRangeSelector: () => import("@/components/common/DateRangeSelector"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    assetDetails: {
      type: Object,
      required: true,
    },
    assetDateRange: {
      type: Object,
    },
    assetActiveRange: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      page: 1,
      pageSize: 25,
      itemsResults: [],
      scrollToTop: new Date().toISOString(),
      timeRangeOptions: TIME_OPTIONS,
      selectedDateRange: {
        start:
          this.assetDateRange?.start ??
          getRelativeDate(
            TIME_OPTIONS[3].value.isRelative,
            TIME_OPTIONS[3].value.start
          ),
        end:
          this.assetDateRange?.end ??
          getRelativeDate(
            TIME_OPTIONS[3].value.isRelative,
            TIME_OPTIONS[3].value.end
          ),
      },
      activeTimeRange: TIME_OPTIONS[3].value,
      categoriesSelected: [],
      categories: ["Crime", "Terrorism", "Unrest", "War"],
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    unit() {
      return this.preferences.poisDistUnits === "miles" ? "mi" : "km";
    },
    startDate() {
      return (
        this.selectedDateRange?.start ??
        getRelativeDate(
          this.activeTimeRange.isRelative,
          this.activeTimeRange.start
        )
      );
    },
    endDate() {
      return (
        this.selectedDateRange?.end ??
        getRelativeDate(
          this.activeTimeRange.isRelative,
          this.activeTimeRange.end
        )
      );
    },
    pois() {
      return [
        [
          this.assetDetails.longitude,
          this.assetDetails.latitude,
          this.assetDetails?.formatted?.monitorRadius,
          this.assetDetails.id,
        ],
      ];
    },
  },
  async mounted() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      this.lastTimestamp = null;
      this.page = 1;
      this.itemsResults = [];
      await this.getContent(this.page);
    },
    async getContent(page, $state, params) {
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }

      await this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (page, $state, params) {
      let dataParams;
      try {
        this.loading = true;
        this.page = page ?? 1;
        dataParams = this.getParams();
        const result = await getArticles(dataParams, this.lastTimestamp);

        if (result?.features) {
          this.itemsResults = [...this.itemsResults, ...result.features];
        } else {
          this.itemsResults = [];
        }

        if (result) {
          setInfiniteScrollState(
            $state,
            { features: result.features },
            "features"
          );
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        this.page = page;
        this.loading = false;
      }
    }, 100),
    getThresholdTypeFunction(doc) {
      return lodashGet(doc, "document.source") === "twitter"
        ? "twitter"
        : "cluster";
    },
    async handleDateRangeChange(dates) {
      this.selectedDateRange = {
        start: dates.range.start,
        end: dates.range.end,
      };
      await this.initialize();
    },
    async handleChangeCategories() {
      await this.initialize();
    },
    getParams() {
      return {
        pageSize: this.pageSize,
        pageOffset: (this.page - 1) * this.pageSize,
        sortDirection: "desc",
        sources: "political",
        eventType: "verified",
        severityMin: 0,
        severityMax: 10,
        pois: JSON.stringify(this.pois),
        poisDistUnits: this.unit,
        radius: this.assetDetails?.formatted?.monitorRadius,
        start: dayjs(this.startDate).startOf("day").toISOString(),
        end: dayjs(this.endDate).endOf("day").toISOString(),
        topics: this.categoriesSelected
          ?.map((category) => category.toLowerCase())
          ?.join(","),
      };
    },
  },
  watch: {
    params: {
      handler(newParams, oldParams) {
        const currParams = omit(newParams, "pageOffset");
        const prevParams = omit(oldParams, "pageOffset");

        if (!lodashIsEqual(currParams, prevParams)) {
          this.lastTimestamp = new Date().toISOString();
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
<style scoped></style>
